<script context="module">
  /**
   * incrementally sanitize and format date input
   *
   * @param input
   */
  export function formatDateInput(input = "") {
    const regex = /\d+/g;
    const digits = input.match(regex)?.join("") ?? "";

    let formatted = "";

    [...digits].slice(0, 8).forEach((char, index) => {
      formatted += char;
      if ([2, 4].includes(index + 1)) {
        formatted += "/";
      }
    });

    return formatted;
  }

  /**
   * incrementally sanitize and format time input
   *
   * @param {string} input
   */
  export function formatTimeInput(input = "") {
    const regex = /\d+/g;
    const digits = input.match(regex)?.join("") ?? "";
    const lastChar = input.at(-1) || "";

    let formatted = "";

    if (lastChar === ":") {
      // allow manual input of colon
      formatted = [1, 2].includes(digits.length) ? input : input.substring(0, input.length - 1);

      // prepend 0
      if (digits.length === 1) {
        formatted = `0${input}`;
      }
    } else if (input.length === digits.length && input.length === 2) {
      // append colon
      formatted = `${input}:`;
    } else if (input.indexOf(":") === 2 && input.length === 5 && digits.length === 4) {
      // append space
      formatted = `${input} `;
    } else if (input.indexOf(":") === 2 && input.indexOf(" ") === 5) {
      if (input.length === 6) {
        // leave as is: "hh:mm "
        formatted = input;
      } else if (input.length === 7) {
        if (["A", "P"].includes(lastChar.toUpperCase())) {
          // append M
          formatted = `${input}M`;
        } else {
          // replace invalid char w/ A
          formatted = `${input.substring(0, input.length - 1)}A`;
        }
      } else if (input.length === 8) {
        // force last char to be M
        formatted = `${input.substring(0, input.length - 1)}M`;
      }
    } else {
      // remove all other invalid characters
      // eslint-disable-next-line regexp/no-dupe-characters-character-class
      formatted = input.replaceAll(/[^\d|\s|:|(a|p|m)]/gi, "");
      if (formatted.length > 0 && digits.length === 0) {
        formatted = "";
      }
    }

    return formatted.toUpperCase().substring(0, 8);
  }
</script>

<script>
  import Input from "./Input.svelte";

  /** @type {"date" | "time"} */
  export let type = "date";

  /** @type {string} */
  export let defaultValue = "";

  /** @type {string} */
  export let helpText = "";

  const helpTexts = {
    date: "Please enter a date in the format MM/DD/YYYY.",
    time: "Please enter a time in the format HH:MM AM/PM."
  };

  const constraints = {
    date: {
      maxlength: 10,
      pattern: "(0[1-9]|1[0,1,2])(/)(0[1-9]|[12][0-9]|3[01])(/)(19|20)\\d{2}"
    },
    time: {
      maxlength: 8,
      pattern: "(((0[1-9])|(1[0-2])):([0-5])([0-9])\\s(A|P)M)"
    }
  };

  const placeholders = {
    date: "mm/dd/yyyy",
    time: "--:-- --"
  };

  const inputFormatters = {
    date: formatDateInput,
    time: formatTimeInput
  };

  let validatedInput = defaultValue;
  let dynamicProps = {
    shouldAnimate: !!defaultValue
  };

  /**
   * @param {Event} e
   */
  function handleFocus(e) {
    if (e.type === "focusin") {
      dynamicProps = {
        shouldAnimate: true,
        placeholder: placeholders[type]
      };
    } else if (!e.target.value) {
      dynamicProps = {
        shouldAnimate: false,
        placeholder: ""
      };
    }
  }

  /**
   * @param {Event} e
   */
  function handleChange(e) {
    const input = e.target.value;
    if (!input) {
      dynamicProps = {
        shouldAnimate: false,
        placeholder: ""
      };
    }
  }

  let key;
  function handleKeyPress(e) {
    const input = e.target.value;

    validatedInput = inputFormatters[type](input);

    if (!input || !validatedInput) {
      dynamicProps = {
        ...dynamicProps,
        shouldAnimate: true,
        placeholder: placeholders[type]
      };
    } else if (input !== validatedInput) {
      dynamicProps = {
        ...dynamicProps,
        autofocus: true
      };

      if (validatedInput.at(-1) !== e.key) {
        key = e.key; // force component update
      }
    }
  }
</script>

{#key key}
  <Input
    type="text"
    defaultValue={validatedInput}
    on:keyup={handleKeyPress}
    on:invalid
    on:click
    on:focusin={handleFocus}
    on:focusout={handleFocus}
    on:change={handleChange}
    helpText={helpText || helpTexts[type]}
    {...constraints[type]}
    {...dynamicProps}
    {...$$restProps}
  >
    <slot />
  </Input>
{/key}
